import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { mockProducts } from '@/lib/mockData';
import { useAuth } from '@/contexts/AuthContext';
import { Product } from '@/types/product';

const parseIntegrationTags = (text?: string | null): string[] => {
  if (!text) return [];
  return text
    .split(/[,+/&]/)        // split on commas, plus, slashes, ampersands
    .map(t => t.trim())
    .filter(t => t.length > 1);
};

export const useProducts = () => {
  const { isEditor } = useAuth();

  return useQuery({
    queryKey: ['products', isEditor],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          product_plans (price)
        `)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching products:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      return data.map(product => {
        const plans = (product.product_plans || []) as any[];
        const plansCount = plans.length;
        const plansWithPrice = plans.filter(p => Number(p.price) > 0);
        const startingPrice = plansWithPrice.length > 0 
          ? Math.min(...plansWithPrice.map(p => Number(p.price)))
          : (product.price ? Number(product.price) : null);

        return {
          id: product.id,
          slug: product.slug,
          isFromDatabase: true,
          title: product.title,
          subtitle: product.subtitle || '',
          description: product.description || '',
          image: product.image || '',
          gallery: product.gallery as string[],
          price: product.price ? Number(product.price) : null,
          msrp: product.msrp ? Number(product.msrp) : null,
          price_duration: product.price_duration || 'mo',
          rating: Number(product.rating),
          review_count: product.review_count,
          categories: (product.categories as string[]) || [],
          tags: product.tags as string[],
          is_select: product.is_select,
          is_hot: (product as any).is_hot || false,
          features: product.features as string[],
          is_published: product.is_published,
          service_type: (product as any).service_type,
          best_for: (product as any).best_for as string[] || [],
          integrations_text: (product as any).integrations_text,
          integration_tags: parseIntegrationTags((product as any).integrations_text),
          
          // Course-specific fields
          curriculum: (product as any).curriculum,
          learning_outcomes: (product as any).learning_outcomes,
          instructor_info: (product as any).instructor_info,
          course_duration: (product as any).course_duration,
          skill_level: (product as any).skill_level,
          certificate_included: (product as any).certificate_included,
          access_type: (product as any).access_type,
          access_url: (product as any).access_url,
          access_instructions: (product as any).access_instructions,
          total_video_hours: (product as any).total_video_hours,
          downloadables_count: (product as any).downloadables_count,
          preview_lesson_url: (product as any).preview_lesson_url,
          student_count: (product as any).student_count,
          profile_image: (product as any).profile_image,
          sort_order: product.sort_order || 999,
          
          plansCount,
          startingPrice,
          plans: [],
          reviews: [],
        };
      });
    },
    staleTime: 5 * 60 * 1000,       // 5 min fresh
    gcTime: 30 * 60 * 1000,         // 30 min in memory
    refetchOnMount: false,           // Don't refetch if cached
    refetchOnWindowFocus: false,     // Don't refetch on tab focus
  });
};

export const useProduct = (id: string) => {
  const { isEditor } = useAuth();

  return useQuery({
    queryKey: ['product', id, isEditor],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          product_plans (*),
          product_reviews (*),
          product_qa (*)
        `)
        .eq('slug', id)
        .single();

      if (error || !data) {
        const mockProduct = mockProducts.find(p => p.id === id);
        return mockProduct || null;
      }

      // Map database fields to match mock product interface
      const dataWithServiceFields = data as any; // Type assertion until auto-generated types update
      return {
        id: data.id,
        slug: data.slug,
        title: data.title,
        subtitle: data.subtitle || '',
        description: data.description || '',
        image: data.image || '',
        gallery: (data.gallery as string[]) || [],
        price: data.price ? Number(data.price) : null,
        msrp: data.msrp ? Number(data.msrp) : null,
        price_duration: dataWithServiceFields.price_duration || 'mo',
        rating: Number(data.rating),
        review_count: data.review_count,
        categories: (data.categories as string[]) || [],
        tags: (data.tags as string[]) || [],
        is_select: data.is_select,
        is_hot: dataWithServiceFields.is_hot || false,
        features: (data.features as string[]) || [],
        is_published: data.is_published,
        best_for: dataWithServiceFields.best_for || [],
        service_type: dataWithServiceFields.service_type as 'product' | 'service' | undefined,
        demo_video_url: dataWithServiceFields.demo_video_url,
        booking_url: dataWithServiceFields.booking_url,
        website_url: dataWithServiceFields.website_url,
        profile_image: dataWithServiceFields.profile_image,
        integrations_text: dataWithServiceFields.integrations_text,
        integration_tags: parseIntegrationTags(dataWithServiceFields.integrations_text),
        feature_sections: dataWithServiceFields.feature_sections || [],
        show_feature_sections: dataWithServiceFields.show_feature_sections || false,
        feature_highlights: dataWithServiceFields.feature_highlights,
        service_highlights: dataWithServiceFields.service_highlights,
        podcast_audio_url: dataWithServiceFields.podcast_audio_url,
        podcast_title: dataWithServiceFields.podcast_title,
        roi_metrics: dataWithServiceFields.roi_metrics,
        setup_info: dataWithServiceFields.setup_info,
        testimonial_videos: dataWithServiceFields.testimonial_videos,
        show_testimonials: dataWithServiceFields.show_testimonials,
        agent_questions: dataWithServiceFields.agent_questions,
        company_info: dataWithServiceFields.company_info,
        founder_updates: dataWithServiceFields.founder_updates,
        plans: (data.product_plans || []).map((plan: any) => ({
          name: plan.name,
          price: Number(plan.price),
          originalPrice: plan.original_price ? Number(plan.original_price) : undefined,
          features: (plan.features as string[]) || [],
        })),
        reviews: (data.product_reviews || []).map((review: any) => ({
          id: review.id,
          author: review.author,
          rating: Number(review.rating),
          date: review.date,
          title: review.title,
          content: review.content,
          verified: review.verified,
          user: {
            name: review.author,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author)}&background=random`,
            isPlusMember: false,
            isVerifiedPurchaser: review.verified,
            dealsBought: 1,
            memberSince: new Date(review.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          }
        })),
        decision_qa: (data.product_qa || []).map((qa: any) => ({
          question: qa.question,
          answer: qa.answer,
        })),
        at_a_glance: dataWithServiceFields.at_a_glance,
      };
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,       // 5 min fresh
    gcTime: 30 * 60 * 1000,         // 30 min in memory
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
